import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface AggregateStripItem {
  label: string;
  value: React.ReactNode;
}

interface AggregateStripProps {
  items: AggregateStripItem[];
  sort?: { label: string; direction?: "asc" | "desc" };
  className?: string;
}

/**
 * Presentational horizontal strip used above a DataTable. Mirrors
 * `.aggregate-strip` from mockup--opportunity-list.css:227-260.
 * Cells: uppercase label + mono value. Optional right-aligned sort cell.
 * Composed visually with the table below it — top corners rounded, bottom flat.
 */
export function AggregateStrip({
  items,
  sort,
  className,
}: AggregateStripProps) {
  return (
    <div
      className={cn(
        "flex h-10 items-center gap-5 px-4",
        "border border-b-0 border-[color:var(--color-border)]",
        "bg-[color:var(--color-elevated)]",
        "rounded-t-[var(--radius-md)]",
        className,
      )}
    >
      {items.map((item) => (
        <div key={item.label} className="inline-flex items-baseline gap-2">
          <span className="typo-label uppercase text-[color:var(--color-text-md)]">
            {item.label}
          </span>
          <span className="typo-mono tabular-nums text-[color:var(--color-text-hi)]">
            {item.value}
          </span>
        </div>
      ))}
      {sort && (
        <>
          <div className="flex-1" />
          <div className="inline-flex items-center gap-1 typo-label uppercase text-[color:var(--color-text-md)]">
            <span>{sort.label}</span>
            {sort.direction === "asc" ? (
              <ChevronUp className="h-3 w-3" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            )}
          </div>
        </>
      )}
    </div>
  );
}
